import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router'
import {
  JobRoot,
  JobRootRefresh,
} from '@/features/jobs/details/components/JobRoot'
import { type JobRootViewModel } from '@/features/jobs/details/JobRootViewModel'
import { JobListContainer } from '@/features/jobs/details/components/JobList/JobListContainer'
import { JobSidebarContainer } from '@/features/jobs/details/components/JobSidebarContainer'
import { ContentLoading } from '@/components/ContentLoading'
import { ResizeHandle } from '@/components/ResizeHandle'
import { useLoading } from '@/hooks/loading-hooks'
import { usePendingJobId } from '@/features/jobs/details/job-navigation-hooks'
import { useIsSidebarCollapsed } from '@/hooks/viewport-hooks'
import { AccessDeniedView } from '@/components/AccessDeniedView'
import {
  JobCardsContainer,
  type JobCardsContainerProps,
} from '@/features/jobs/details/components/JobCards/JobCardsContainer'
import { isRefreshDesign } from '@/infra/design'
import type { JobDetailsViewLoaderData } from '@/features/jobs/job-route-loaders'
import { useActivation } from '@/hooks/view-model-hooks'
import { useFeatures } from '@/contexts/root-store-context'
import { JobErrorContainer } from '@/features/jobs/components/JobErrorContainer'

export interface JobRootViewContainerProps {
  rootVm: JobRootViewModel
  data: JobDetailsViewLoaderData
  error?: unknown
}

export const JobRootViewContainer = observer(function JobRootViewContainer({
  rootVm,
  data,
  error,
}: JobRootViewContainerProps) {
  const isSwitchingJob = useIsSwitchingJob()
  const [highlightBorder, setHighlightBorder] = useState(false)
  const allowShowingList = !useIsSidebarCollapsed()
  useActivation(rootVm)

  if (isRefreshDesign) {
    const listVisible = allowShowingList && rootVm.jobListVm.panel.showing
    const listWidth = rootVm.jobListVm.panelWidth

    return (
      <JobRootRefresh
        list={listVisible && <JobSidebarContainer vm={rootVm.jobListVm} />}
        listWidth={listWidth.width}
        listResizeHandle={
          listVisible && (
            <ResizeHandle
              width={listWidth.width}
              minWidth={listWidth.bounds.minWidth}
              maxWidth={listWidth.bounds.maxWidth}
              onResize={listWidth.setWidth}
              onResizeEnd={listWidth.save}
              onReset={listWidth.reset}
              aria-label="Resize my jobs"
            />
          )
        }
      >
        <JobRootContentContainer
          data={data}
          error={error}
          isSwitchingJob={isSwitchingJob}
          onJobListToggleHoverChange={setHighlightBorder}
          isJobListSidebarVisible={listVisible}
          onJobListSidebarToggle={
            allowShowingList ? rootVm.jobListVm.toggleVisibility : undefined
          }
          onPreviousJob={
            rootVm.hasPreviousJob ? rootVm.viewPreviousJob : undefined
          }
          onNextJob={rootVm.hasNextJob ? rootVm.viewNextJob : undefined}
        />
      </JobRootRefresh>
    )
  }

  return (
    <JobRoot
      list={allowShowingList && <JobListContainer vm={rootVm.jobListVm} />}
      isListCollapsed={!rootVm.jobListVm.panel.showing}
      highlightBorder={highlightBorder}
    >
      <JobRootContentContainer
        data={data}
        error={error}
        isSwitchingJob={isSwitchingJob}
        onJobListToggleHoverChange={setHighlightBorder}
        isJobListSidebarVisible={rootVm.jobListVm.panel.showing}
        onJobListSidebarToggle={
          allowShowingList ? rootVm.jobListVm.toggleVisibility : undefined
        }
      />
    </JobRoot>
  )
})

interface JobRootContentContainerProps extends Omit<
  JobCardsContainerProps,
  'vm'
> {
  data: JobDetailsViewLoaderData
  error?: unknown
  isSwitchingJob: boolean
}

const JobRootContentContainer = observer(function JobRootContentContainer({
  data,
  error,
  isSwitchingJob,
  ...rest
}: JobRootContentContainerProps) {
  const { shellInfo } = useFeatures(({ shell }) => ({
    shellInfo: shell.shellInfo,
  }))

  if (isSwitchingJob) {
    return <ContentLoading />
  }

  if (error) {
    return <JobErrorContainer error={error} />
  }

  if (!data) {
    return <ContentLoading />
  }

  if ('denied' in data) {
    return (
      <AccessDeniedView
        corporateMode={shellInfo.corporateMode}
        title={data.denied}
      />
    )
  }

  return <JobCardsContainer vm={data.vm} {...rest} />
})

/**
 * Whether a navigation to another job is pending.
 */
function useIsSwitchingJob() {
  const { jobId } = useParams()
  const nextJobId = usePendingJobId()

  // Cached jobs resolve almost immediately, so don't flash the spinner for them.
  return useLoading(Boolean(nextJobId && nextJobId !== jobId), 20)
}
